import { useLoaderData } from "react-router";
import { motion } from "framer-motion";
import { MapPin, ArrowRight } from "lucide-react";
import { brandColors as colors, usePalette } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import { fadeUp, scaleIn, stagger } from "@/lib/animations";
import { loadRetreats, type RetreatSummary, type RetreatsResponse } from "@/lib/retreats";
import Footer from "@/components/ui/Footer";
import Blob from "@/components/ui/Blob";
import RetreatLink from "@/components/retreats/RetreatLink";

type Retreat = RetreatSummary;

function dateRange(start: string, end: string): string {
  const s = new Date(start);
  const e = new Date(end);
  const sameMonth = s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear();
  const fmt = (d: Date, opts: Intl.DateTimeFormatOptions) =>
    d.toLocaleDateString(undefined, opts);
  if (sameMonth) {
    return `${fmt(s, { month: "long" })} ${s.getDate()}–${e.getDate()}, ${s.getFullYear()}`;
  }
  return `${fmt(s, { month: "short", day: "numeric" })} – ${fmt(e, { month: "short", day: "numeric", year: "numeric" })}`;
}

function RetreatsHero() {
  const theme = usePalette();
  return (
    <section className="relative overflow-hidden pt-32 pb-12" style={{ backgroundColor: colors.cream }}>
      <Blob className="w-96 h-96 -top-20 -left-10" color={theme.blob1} />
      <Blob className="w-72 h-72 top-10 right-0" color={theme.blob3} />

      <motion.div
        initial="hidden"
        animate="visible"
        variants={stagger}
        className="relative z-10 mx-auto max-w-5xl px-6 text-center"
      >
        <motion.p
          variants={fadeUp}
          custom={0}
          className="mb-3 text-xs uppercase"
          style={{ fontFamily: fonts.body, fontWeight: 800, letterSpacing: "0.2em", color: theme.accent }}
        >
          Gather + create
        </motion.p>
        <motion.h1
          variants={fadeUp}
          custom={1}
          className="text-5xl sm:text-6xl lg:text-7xl"
          style={{ fontFamily: fonts.logo, fontWeight: 400, color: colors.navy, lineHeight: 1.1 }}
        >
          Sewing{" "}
          <span style={{ fontFamily: fonts.heading, color: theme.warm }}>retreats</span>
        </motion.h1>
        <motion.p
          variants={fadeUp}
          custom={2}
          className="mx-auto mt-6 max-w-2xl text-base"
          style={{
            fontFamily: fonts.body,
            fontWeight: 500,
            color: `${colors.navy}b3`,
            letterSpacing: "0.03em",
            lineHeight: 1.7,
          }}
        >
          A few times a year we gather in person to sew, shop, and slow down together —
          weekends full of creativity, community, and a little Chelsea-style fun.
        </motion.p>
      </motion.div>
    </section>
  );
}

export function retreatsLoader() {
  return loadRetreats();
}

export function Retreats() {
  const { retreats } = useLoaderData() as RetreatsResponse;

  const upcoming = retreats.filter((r) => r.status === "open" || r.status === "sold-out");
  const past = retreats.filter((r) => r.status === "past");

  return (
    <div className="min-h-dvh flex flex-col" style={{ backgroundColor: colors.cream }}>
      <RetreatsHero />

      <main className="flex-1 w-full max-w-6xl mx-auto px-6 pb-16">
        <motion.section initial="hidden" animate="visible" variants={stagger}>
          <motion.h2
            variants={fadeUp}
            custom={0}
            className="text-2xl mb-4"
            style={{ fontFamily: fonts.logo, color: colors.navy }}
          >
            Upcoming
          </motion.h2>
          {upcoming.length === 0 ? (
            <motion.p variants={fadeUp} custom={1} style={{ fontFamily: fonts.body, color: `${colors.navy}aa` }}>
              No upcoming retreats. Subscribe to the newsletter to hear about the next one.
            </motion.p>
          ) : (
            <RetreatGrid retreats={upcoming} />
          )}

          {past.length > 0 && (
            <>
              <motion.h2
                variants={fadeUp}
                custom={2}
                className="text-2xl mt-12 mb-4"
                style={{ fontFamily: fonts.logo, color: `${colors.navy}88` }}
              >
                Past retreats
              </motion.h2>
              <RetreatGrid retreats={past} muted />
            </>
          )}
        </motion.section>
      </main>
      <Footer />
    </div>
  );
}

/* Brand-palette gradient fills for retreats without a hero photo. Assigned
   deterministically by index so the grid looks intentional. */
const GRADIENT_FILLS = [
  `linear-gradient(155deg, ${colors.sage}, ${colors.forestGreen} 62%, ${colors.tan})`,
  `linear-gradient(155deg, ${colors.denimBlue}, ${colors.navy} 60%, ${colors.lavender})`,
  `linear-gradient(155deg, ${colors.berryPink}, ${colors.berryRose} 55%, ${colors.tan})`,
  `linear-gradient(155deg, ${colors.warmCream}, ${colors.tan} 55%, ${colors.forestGreen})`,
  `linear-gradient(155deg, ${colors.softLilac}, ${colors.lavender} 50%, ${colors.denimBlue})`,
  `linear-gradient(155deg, ${colors.blushPink}, ${colors.berryPink} 55%, ${colors.berryRose})`,
];

function ctaLabel(status: Retreat["status"]): string {
  if (status === "sold-out") return "Join waitlist";
  if (status === "past") return "View recap";
  return "View retreat";
}

function StatusBadge({ status }: { status: Retreat["status"] }) {
  const base = {
    fontFamily: fonts.body,
    fontWeight: 700,
    fontSize: 10,
    letterSpacing: "0.16em",
    textTransform: "uppercase" as const,
    borderRadius: 999,
    padding: "5px 11px",
  };
  if (status === "sold-out")
    return <span style={{ ...base, background: colors.berryRose, color: "white" }}>Sold out</span>;
  if (status === "past")
    return (
      <span style={{ ...base, background: "rgba(255,255,255,0.2)", color: "white" }}>Past</span>
    );
  return (
    <span style={{ ...base, background: "rgba(168,181,140,0.92)", color: colors.navy }}>
      Booking open
    </span>
  );
}

function RetreatCard({ retreat, index }: { retreat: Retreat; index: number }) {
  // Default to a brand-palette gradient. A heroImageUrl (set via admin) is an
  // optional opt-in override for retreats with a strong photo.
  const isPhoto = Boolean(retreat.heroImageUrl);
  const fill = isPhoto
    ? `url(${retreat.heroImageUrl})`
    : GRADIENT_FILLS[index % GRADIENT_FILLS.length];

  return (
    <motion.div variants={scaleIn} custom={index}>
      <RetreatLink
        slug={retreat.slug}
        to={`/retreats/${retreat.slug}`}
        className="group relative block overflow-hidden"
        style={{
          height: 420,
          borderRadius: 24,
          color: "white",
          textDecoration: "none",
          boxShadow: "0 16px 34px rgba(8,45,61,0.16)",
        }}
      >
        {/* fill (brand gradient by default, photo if opted-in) */}
        <div
          className="absolute inset-0 transition-transform duration-700 group-hover:scale-105"
          style={{ background: fill, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        {/* stitch-dot texture over gradients, for a crafted feel */}
        {!isPhoto && (
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "radial-gradient(rgba(255,255,255,0.9) 1.3px, transparent 1.4px)",
              backgroundSize: "18px 18px",
              opacity: 0.14,
            }}
          />
        )}
        {/* scrim: dark top edge for chip legibility + dark bottom for text */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(8,45,61,0.5) 0%, rgba(8,45,61,0.05) 22%, rgba(8,45,61,0.16) 50%, rgba(8,45,61,0.86) 100%)",
          }}
        />

        {/* top row: date chip + status */}
        <div className="absolute left-[18px] right-[18px] top-[18px] flex items-center justify-between">
          <span
            style={{
              fontFamily: fonts.body,
              fontWeight: 700,
              fontSize: 10,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              background: "rgba(255,248,237,0.95)",
              color: colors.navy,
              borderRadius: 999,
              padding: "6px 12px",
            }}
          >
            {dateRange(retreat.startDate, retreat.endDate)}
          </span>
          <StatusBadge status={retreat.status} />
        </div>

        {/* bottom content */}
        <div className="absolute left-[22px] right-[22px] bottom-[22px]">
          {retreat.tagline && (
            <div
              style={{
                fontFamily: fonts.heading,
                fontSize: 24,
                color: colors.blushPink,
                lineHeight: 1.05,
                marginBottom: 2,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {retreat.tagline}
            </div>
          )}
          <h3 style={{ fontFamily: fonts.logo, fontSize: 29, lineHeight: 1.08, marginTop: 2 }}>
            {retreat.title}
          </h3>
          {retreat.subtitle && (
            <div
              style={{
                fontFamily: fonts.body,
                fontSize: 13,
                fontWeight: 500,
                color: "rgba(255,255,255,0.8)",
                marginTop: 4,
              }}
            >
              {retreat.subtitle}
            </div>
          )}
          <div
            className="flex items-center gap-1.5"
            style={{ fontFamily: fonts.body, fontSize: 14, fontWeight: 600, color: "rgba(255,255,255,0.88)", marginTop: 8 }}
          >
            <MapPin size={15} />
            {retreat.location}
          </div>
          <span
            className="inline-flex items-center gap-2 transition-all group-hover:gap-3"
            style={{
              fontFamily: fonts.button,
              fontWeight: 700,
              fontSize: 14,
              background: "white",
              color: colors.navy,
              borderRadius: 999,
              padding: "11px 22px",
              marginTop: 16,
            }}
          >
            {ctaLabel(retreat.status)} <ArrowRight size={16} />
          </span>
        </div>
      </RetreatLink>
    </motion.div>
  );
}

function RetreatGrid({ retreats, muted }: { retreats: Retreat[]; muted?: boolean }) {
  // No initial/animate here — this grid is orchestrated by the page's stagger
  // container, so its cards cascade in after the section heading (like the hero).
  return (
    <motion.div
      variants={stagger}
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
        gap: "1.5rem",
        opacity: muted ? 0.7 : 1,
      }}
    >
      {retreats.map((r, i) => (
        <RetreatCard key={r.id} retreat={r} index={i} />
      ))}
    </motion.div>
  );
}
